import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  IconCheck,
  IconX,
  IconTerminal2,
  IconLoader2
} from '@tabler/icons-react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';

const CLIAuthPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  
  const [status, setStatus] = useState('pending'); // pending, loading, success, error
  const [error, setError] = useState('');
  const [deviceCode, setDeviceCode] = useState('');
  const [redirecting, setRedirecting] = useState(false);

  const code = searchParams.get('code');

  useEffect(() => {
    if (code) {
      setDeviceCode(code);
      sessionStorage.setItem('cli_device_code', code);
    } else {
      const savedCode = sessionStorage.getItem('cli_device_code');
      if (savedCode) {
        setDeviceCode(savedCode);
      }
    }
  }, [code]);

  useEffect(() => {
    if (authLoading) return;
    
    const currentCode = code || deviceCode;
    
    if (!isAuthenticated && currentCode && !redirecting) {
      setRedirecting(true);
      sessionStorage.setItem('cli_device_code', currentCode);
      navigate('/login?redirect=/cli-auth');
    }
  }, [isAuthenticated, code, deviceCode, navigate, authLoading, redirecting]);

  const handleAuthorize = async () => {
    if (!deviceCode) return;
    
    setStatus('loading');
    try {
      await authAPI.authorizeCLI(deviceCode);
      sessionStorage.removeItem('cli_device_code');
      setStatus('success');
    } catch (err) {
      setError(err.message || 'Ошибка авторизации');
      setStatus('error');
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  // Показываем загрузку пока auth context грузится
  if (authLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-dark-950 flex items-center justify-center px-6">
      <div className="max-w-md w-full rounded-2xl bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 p-10 text-center">
        <IconLoader2 className="w-10 h-10 text-primary-500 animate-spin mx-auto mb-4" />
        <p className="text-gray-600 dark:text-dark-500">Загрузка...</p>
        </div>
      </div>
    );
  }

  if (!deviceCode) {
    return (
      <div className="min-h-screen bg-white dark:bg-dark-950 flex items-center justify-center px-6">
        <div className="max-w-md w-full rounded-2xl bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 p-10 text-center">
          <div className="w-16 h-16 mx-auto mb-6 bg-red-500/10 rounded-xl flex items-center justify-center">
            <IconX className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Неверная ссылка</h1>
          <p className="text-gray-600 dark:text-dark-500 mb-8">
            Код авторизации не найден. Попробуйте запустить команду в CLI заново.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-gray-100 dark:bg-dark-850 border border-gray-200 dark:border-dark-800 hover:border-gray-300 dark:hover:border-dark-700 rounded-lg text-gray-900 dark:text-white transition-colors"
          >
            На главную
          </button>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-white dark:bg-dark-950 flex items-center justify-center px-6">
        <div className="max-w-md w-full rounded-2xl bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 p-10 text-center">
          <div className="w-16 h-16 mx-auto mb-6 bg-emerald-500/10 rounded-xl flex items-center justify-center">
            <IconCheck className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">CLI авторизован!</h1>
          <p className="text-gray-600 dark:text-dark-500 mb-8">
            Вы можете закрыть эту страницу и вернуться в терминал.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-primary-500 hover:bg-primary-600 rounded-lg text-white font-semibold transition-colors"
          >
            Перейти в панель
          </button>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-white dark:bg-dark-950 flex items-center justify-center px-6">
        <div className="max-w-md w-full rounded-2xl bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 p-10 text-center">
          <div className="w-16 h-16 mx-auto mb-6 bg-red-500/10 rounded-xl flex items-center justify-center">
            <IconX className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Ошибка</h1>
          <p className="text-gray-600 dark:text-dark-500 mb-8">{error}</p>
          <button
            onClick={() => setStatus('pending')}
            className="px-6 py-3 bg-primary-500 hover:bg-primary-600 rounded-lg text-white font-semibold transition-colors"
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-dark-950 flex items-center justify-center px-6">
      <div className="max-w-md w-full rounded-2xl bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 p-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-6 bg-primary-500/10 rounded-xl flex items-center justify-center">
            <IconTerminal2 className="w-8 h-8 text-primary-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Авторизация CLI</h1>
          <p className="text-gray-600 dark:text-dark-500">
            Приложение XN Connect CLI запрашивает доступ к вашему аккаунту
          </p>
        </div>

        {/* IconUser info */}
        {user && (
        <div className="p-4 bg-gray-50 dark:bg-dark-950 border border-gray-200 dark:border-dark-800 rounded-xl mb-4">
          <p className="text-gray-600 dark:text-dark-500 text-sm mb-1">Войти как:</p>
          <p className="text-gray-900 dark:text-white font-medium">{user.username}</p>
          </div>
        )}

        {/* Device code */}
        <div className="p-4 bg-gray-50 dark:bg-dark-950 border border-gray-200 dark:border-dark-800 rounded-xl mb-6">
          <p className="text-gray-600 dark:text-dark-500 text-sm mb-1">Код устройства:</p>
          <code className="text-primary-500 font-mono">{deviceCode.substring(0, 8)}...</code>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={handleCancel}
            className="flex-1 py-3 bg-gray-100 dark:bg-dark-850 border border-gray-200 dark:border-dark-800 hover:border-gray-300 dark:hover:border-dark-700 rounded-lg text-gray-900 dark:text-white transition-colors"
          >
            Отмена
          </button>
          <button
            onClick={handleAuthorize}
            disabled={status === 'loading'}
            className="flex-1 py-3 bg-primary-500 hover:bg-primary-600 rounded-lg text-white font-semibold transition-colors disabled:opacity-50 flex items-center justify-center"
          >
            {status === 'loading' ? (
              <IconLoader2 className="w-5 h-5 animate-spin" />
            ) : (
              'Авторизовать'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CLIAuthPage;
